import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Switch,
  Divider,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";

const defaultSettings = {
  emailNotifications: true,
  reviewReminders: true,
  publicProfile: false,
  personalizedRecommendations: true,
};

const settingsConfig = [
  {
    key: "emailNotifications",
    title: "Email Notifications",
    description: "Receive important updates and activity notifications by email.",
  },
  {
    key: "reviewReminders",
    title: "Product Review Reminders",
    description: "Get reminders to share your experience after adding a product.",
  },
  {
    key: "publicProfile",
    title: "Public Profile",
    description: "Allow other users to view your profile and shared reviews.",
  },
  {
    key: "personalizedRecommendations",
    title: "Personalized Recommendations",
    description: "Show product suggestions based on your interests and activity.",
  },
];

const UserSettings = ({
  initialSettings = defaultSettings,
  onSave,
}) => {
  const [settings, setSettings] = useState({
    ...defaultSettings,
    ...initialSettings,
  });

  const [savedSettings, setSavedSettings] = useState({
    ...defaultSettings,
    ...initialSettings,
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const hasChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(savedSettings);
  }, [settings, savedSettings]);

  const handleToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleReset = () => {
    setSettings(savedSettings);
  };

  const handleSave = async () => {
    const payload = {
      email_notifications: settings.emailNotifications,
      review_reminders: settings.reviewReminders,
      public_profile: settings.publicProfile,
      personalized_recommendations: settings.personalizedRecommendations,
    };

    try {
      // API call can be handled from parent component
      await onSave?.(payload);

      setSavedSettings(settings);
      setShowSuccess(true);
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        mx: "auto",
        p: {
          xs: 2,
          sm: 3,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: "18px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
            },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: {
                xs: "20px",
                sm: "24px",
              },
              color: "text.primary",
            }}
          >
            Settings
          </Typography>

          <Typography
            sx={{
              mt: 0.75,
              fontSize: "14px",
              color: "text.secondary",
            }}
          >
            Manage your website preferences and control what you want to enable.
          </Typography>
        </Box>

        <Divider />

        {/* Settings List */}
        <Stack divider={<Divider />}>
          {settingsConfig.map((item) => (
            <Box
              key={item.key}
              sx={{
                px: {
                  xs: 2,
                  sm: 3,
                },
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: "13px",
                    lineHeight: 1.5,
                    color: "text.secondary",
                  }}
                >
                  {item.description}
                </Typography>
              </Box>

              <Switch
                checked={Boolean(settings[item.key])}
                onChange={() => handleToggle(item.key)}
                inputProps={{
                  "aria-label": item.title,
                }}
              />
            </Box>
          ))}
        </Stack>

        <Divider />

        {/* Footer Buttons */}
        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
            },
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexDirection: {
              xs: "column-reverse",
              sm: "row",
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              backgroundColor: "#111827",
              "&:hover": {
                backgroundColor: "#000000",
              },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setShowSuccess(false)}
          sx={{
            borderRadius: "10px",
          }}
        >
          Settings updated successfully.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSettings;